import { useEffect, useState } from "react";
import "./CalculationPopup.css";

const fmtAmount = (currency, value, territoryId) =>
  `${currency || ""}${Number(value).toLocaleString("en-GB", !currency
    ? { maximumFractionDigits: 6 }
    : territoryId === 6 ? { maximumFractionDigits: 0 } : undefined)}`;

// --- "View Calculation" formula panel -------------------------------------
//
// Ported verbatim (text, headings, bold spans, territory-specific variants)
// from old Angular's calculation-panel.component.html - that template is
// where all of this copy actually lived. The generic "*_formula key ->
// label" renderer this replaced only ever showed label/value pairs, so all
// of the static explanatory copy (section headings + description
// paragraphs) was simply never ported the first time round.
//
// Each `show_*` boolean below comes straight off the matching territory's
// *_detail.sql file (uk_detail.sql, ireland_detail.sql, india_detail.sql,
// romania_detail.sql, sa_detail.sql, brazil_detail.sql) - routes.py's
// /api/calculation-detail already selects the correct file for the given
// territory_id, so only that territory's flags/fields ever come back true.
//
// A few rate fields (e.g. UIF/SDL/WCA % for South Africa) were never given
// their own text alias in the SQL - old Angular's own template already
// only ever fell back to its hardcoded default for those (calculationData.x
// || 'default%'), since it also had no such field to read. Kept identical.
//
// NOT ported: the "highlight changes vs last upload" diff/toggle feature
// (isChanged()/getOldValue() in the old component) - that's a separate,
// unrequested feature layered on top of this panel, not part of the
// missing-text fix. Can be added as its own piece of work if wanted.
function CalculationDetailSections({ detail, territoryId }) {
  if (!detail) return null;
  const d = detail;

  return (
    <>
      {d.show_base_salary && (
        <Section
          heading={<>Base salary{territoryId === 1 && <span className="calcpop-heading-note"> (Basic Pay)</span>}</>}
          description={
            territoryId === 1
              ? "Fixed base salary used to calculate several statutory benefits. Usually 40% of CTC (Cost to Company)."
              : "The employee's gross annual salary before tax."
          }
        />
      )}

      {d.show13th_month_salary && (
        <Section
          heading="13th Month Salary (statutory)"
          description="Mandatory annual bonus equivalent to one additional monthly salary, required by Brazilian labour law. Accrual rate equals 1/12 of annual salary."
          formulas={[{ label: "13th Month Salary =", value: d.month13_salary_formula }]}
        />
      )}

      {d.show_vacation_bonus && (
        <Section
          heading="Vacation Bonus (statutory)"
          description="Additional payment equal to one-third of the monthly salary, paid during annual leave as required by law (férias). Represents 1/3 of 1/12 of annual base (2.77% accrual)."
          formulas={[{ label: "Vacation Bonus =", value: d.vacation_bonus_formula }]}
        />
      )}

      {territoryId !== 6 && d.show_performance_bonus && (
        <Section
          heading="Performance Bonus (as per CBA)"
          description="Variable bonus that may be required under the applicable Collective Bargaining Agreement or company policy."
        />
      )}

      {territoryId !== 6 && d.show_life_insurance && (
        <Section
          heading="Life insurance (as per CBA)"
          description="Life insurance that may be required under the applicable Collective Bargaining Agreement or company policy."
        />
      )}

      {d.show_social_security && (
        <Section
          heading="Social Security (INSS-Employer)"
          description="Employer contribution to Brazil's public social security system (typically 20% of payroll). Applied on total gross salary."
          formulas={[{ label: "Social Security (INSS-Employer) =", value: d.social_security_formula }]}
        />
      )}

      {d.show_severance_fund && (
        <Section
          heading="Severance Fund (FGTS)"
          description="Mandatory employer contribution of 8% of salary deposited into the employee's FGTS account."
          formulas={[{ label: "Severance Fund (FGTS) =", value: d.severance_fund_formula }]}
        />
      )}

      {d.show_fgts_termination_accrual && (
        <Section
          heading="FGTS Termination Accrual-Employer Accrual"
          description="Provision for the 40% FGTS termination penalty, typically accrued at 3.2% (40% of the 8% FGTS rate) of gross salary."
          formulas={[{ label: "FGTS Termination Accrual - Employer Accrual =", value: d.fgts_termination_accrual_formula }]}
        />
      )}

      {d.show_workers_accident_insurance && (
        <Section
          heading="Workers Accident Insurance (RAT)"
          description="Employer insurance contribution covering workplace accident risks. Rate ranges from 1-3% depending on industry risk level; 1% applied here."
          formulas={[{ label: "Workers Accident Insurance (RAT) =", value: d.workers_accident_insurance_formula }]}
        />
      )}

      {d.show_sistema_or_third_party_contributions && (
        <Section
          heading="Sistema S/Third-party contributions"
          description="Payroll contributions funding national training and social programs (SENAI, SESI, SENAC, etc.). Approximately 5.8% of gross salary, applied on total gross salary."
          formulas={[{ label: "Sistema S/Third-party contributions =", value: d.sistema_or_third_party_contributions_formula }]}
        />
      )}

      {d.show_special_allowance && (
        <Section
          heading="Special Allowance"
          description="Flexible salary component used to balance the total compensation package and fully taxable under Indian payroll regulations."
        />
      )}

      {d.show_house_rent_allowance && (
        <Section
          heading="House Rent Allowance (HRA)"
          description={
            <>
              Allowance provided to support rental housing costs; tax treatment depends on the employee's rent payments and eligibility under Indian tax rules. Normally <strong>40-50% of Basic.</strong>
            </>
          }
        />
      )}

      {d.show_commuting_allowance && (
        <Section
          heading="Commuting Allowance"
          description="Allowance provided to cover employee transportation or commuting expenses."
        />
      )}

      {d.show_employer_pf && (
        <Section
          heading="Employer PF"
          description={
            <>
              Employer contribution to the <strong>Employees' Provident Fund (EPF),</strong> a mandatory retirement savings scheme, typically <strong>12% of eligible salary components.</strong> It is often <strong>capped on a wage ceiling of ₹ 15,000 per month.</strong>
            </>
          }
          formulas={[{ label: "So:", value: "₹ 15,000 × 12% = ₹ 1,800/month; ₹ 1,800 x 12 = ₹ 21,600/year" }]}
        />
      )}

      {d.show_medical_insurance && (
        <Section
          heading="Medical insurance"
          description={
            territoryId === 1
              ? "The annual cost of employer-provided medical insurance. Usually 0.5-2% of salary"
              : "The annual cost of employer-provided medical insurance."
          }
        />
      )}

      {d.show_bonus && (
        <Section
          heading={territoryId === 1 ? "Bonus" : "Bonus (incl. tax)"}
          description={
            territoryId === 1
              ? <>Additional compensation that may be statutory under the <strong>Payment of Bonus Act</strong> or provided as a company performance bonus.</>
              : "Annual bonus paid to the employee, including any applicable taxes borne by the employer."
          }
        />
      )}

      {d.show_disability_tax && (
        <Section
          heading="Disability Tax"
          description="Statutory employer contribution payable under Romanian law if the required employment quota for persons with disabilities is not met."
        />
      )}

      {d.show_gratuity && (
        <Section
          heading="Gratuity"
          description={
            <>
              Statutory end-of-service benefit payable to employees with <strong>five or more years of service,</strong> accrued as part of the employee's compensation cost. Required under the <strong>Payment of Gratuity Act, 1972.</strong> Basic (15/26)*(1/12)=<strong>4.81%</strong>
            </>
          }
        />
      )}

      {d.show_meal_vouchers && (
        <Section
          heading="Meal Vouchers"
          description={territoryId === 6 ? "Optional meal allowance benefit." : "The annual cost of employer-provided meal vouchers."}
        />
      )}

      {d.show_employer_government_contribution && (
        <Section
          heading="Employer Government Contribution"
          description={`Mandatory employer labour contribution calculated at ${d.employer_contribution || "2.25%"} of gross salary.`}
          formulas={[{ label: "Employer Contribution:", value: d.employer_contribution_formula }]}
        />
      )}

      {d.show_employer_pension && (
        <Section
          heading="Employer Pension"
          description={`Employer contribution to the employee's pension fund calculated as ${d.employer_pension || "4%"} of gross salary.`}
          formulas={[{ label: "Employer Pension:", value: d.employer_pension_formula }]}
        />
      )}

      {d.show_uif_employer_contribution && (
        <Section
          heading="UIF-Employer Contribution"
          description="Unemployment Insurance Fund contribution payable by the employer. Calculated at 1% of salary, subject to the statutory earnings cap."
          formulas={[{ label: "UIF (Employer):", value: d.uif_employer_formula }]}
        />
      )}

      {d.show_skills_development_levy && (
        <Section
          heading="Skills Development Levy (SDL)"
          description="SDL is 1% of total remuneration if the company has a total annual payroll above a certain threshold (R500,000)"
          formulas={[
            { label: "SDL:", value: "1% of (Base + Medical)" },
            ...(d.sdl_employer_formula ? [{ label: "SDL:", value: d.sdl_employer_formula }] : []),
          ]}
        />
      )}

      {d.show_workers_compensation && (
        <Section
          heading="WCA-Workers' Compensation"
          description="Employer contribution to Workers' Compensation Fund, calculated at 0.5% of salary (industry dependent)."
          formulas={
            d.wca_employer_formula
              ? [
                  { label: "Capped Max WCA:", value: "R 4842 per year" },
                  { label: "WCA:", value: d.wca_employer_formula },
                ]
              : []
          }
        />
      )}

      {d.show_health_insurance && (
        <Section heading="Health Insurance" description="Employer-provided private medical insurance benefit." />
      )}

      {territoryId === 6 && d.show_life_insurance && (
        <Section
          heading="Life insurance (as per CBA)"
          description="Life insurance that may be required under the applicable Collective Bargaining Agreement or company policy."
        />
      )}

      {d.show_transportation_allowance && (
        <Section
          heading="Transportation Allowance"
          description="Employer-provided transportation allowance benefit."
        />
      )}

      {d.show_plr && (
        <Section
          heading="Profit and Loss Redistribution (PLR)"
          description="Annual employer cost of employee profit-sharing (PLR), based on company policy or collective bargaining agreements (equivalent to one month's salary per year)."
          formulas={[{ label: "PLR =", value: d.plr_formula }]}
        />
      )}

      {d.show_margin && (
        <Section heading="Margin" description="Commercial margin applied to the UK Sub Total." />
      )}

      {d.show_employee_pay_related_social_insurance && (
        <Section
          heading="Employer Pay-Related Social Insurance (PRSI)"
          description={
            <>
              Standard employer PRSI rate is {d.prsi || "11.25%"} on earnings above the threshold (~€527/week)- applies on the full salary amount once that threshold is exceeded. Employer PRSI on BIK (Medical Insurance): {d.prsi || "11.25%"} (if treated as taxable benefit)
            </>
          }
          formulas={[
            { label: "Salary PRSI:", value: d.salary_prsi_formula },
            { label: "Benefits PRSI:", value: d.benefits_prsi_formula },
            ...(d.total_prsi != null ? [{ label: "Total PRSI =", value: d.total_prsi, bold: true }] : []),
          ]}
        />
      )}

      {d.show_employer_ni && (
        <Section
          heading="Employer National Insurance"
          description={`Calculated on salary above £5,000 at ${d.eni || "15%"}, plus ${d.benifits_ni || "13.8%"} on the medical insurance benefit, in line with current UK rules.`}
          formulas={[
            { label: "Salary NI:", value: d.salary_ni_formula },
            { label: "Benefits NI:", value: d.benefits_ni_formula },
            ...(d.total_eni != null ? [{ label: "Total ENI =", value: d.total_eni, bold: true }] : []),
          ]}
        />
      )}

      {d.show_pension_contribution && (
        <Section
          heading="Employer pension contribution (statutory)"
          description={
            <>
              Calculated as {d.pension || "3%"} of on qualifying earnings, not full salary.<br />
              Qualifying earnings band (2025/26): Lower limit: £6,240 - Upper limit: £50,270.<br />
              If salary exceeds the upper qualifying limit, pension is capped: £50,270 - £6,240 = £44,030 qualifying earnings
            </>
          }
          formulas={[{ label: "Employer pension contribution:", value: d.pension_formula }]}
        />
      )}

      {d.show_fully_inclusive_cost && (
        <Section
          heading="Fully Inclusive Cost"
          description="The total cost to the company for this role over a year."
          formulas={[{ label: "Fully Inclusive Cost:", value: d.fully_inclusive_cost_formula, bold: true }]}
        />
      )}
    </>
  );
}

// One heading + description + (optional) formula line(s), styled to match
// the new popup's headings (same font/colour treatment as the rest of
// CalculationPopup, not the old app's Bootstrap table look).
function Section({ heading, description, formulas = [] }) {
  return (
    <div className="calcpop-section">
      <div className="calcpop-section-header">{heading}</div>
      {description && <div className="calcpop-section-desc">{description}</div>}
      {formulas.filter((f) => f && f.value != null && f.value !== "").map((f, i) => (
        <div className={`calcpop-formula-line${f.bold ? " calcpop-formula-bold" : ""}`} key={i}>
          <span className="calcpop-formula-label">{f.label}</span>
          <span className="calcpop-formula-value">{f.value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * data: { territoryId, timePeriodId, baseAmount, jobTitleId, label, regionId? }
 * regionId is only set for a region-accordion row (uses the *ByTerritoryRegion
 * employee/company count route instead) - not currently wired anywhere in
 * CostBandTable.jsx (region rows don't open this popup yet, scoped out
 * earlier), but the component supports it for interface parity with the
 * real calculation(...) handler's signature.
 */
export default function CalculationPopup({ data, onClose }) {
  const [fields, setFields] = useState([]);
  const [loadingBreakdown, setLoadingBreakdown] = useState(true);
  const [employeeData, setEmployeeData] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);

  const [showCalculation, setShowCalculation] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Ported from overlay-panel.component.ts's loadData(): breakdown +
  // employee/company count + history all fetched together when the popup
  // opens (Promise.all in the real code). Detail (the formula panel) is
  // NOT fetched here - only lazily, on first "View Calculation" click.
  useEffect(() => {
    if (!data) return;
    setLoadingBreakdown(true);
    setShowCalculation(false);
    setDetail(null);
    setShowHistory(false);

    const breakdownParams = new URLSearchParams({
      territory_id: data.territoryId,
      time_period_id: data.timePeriodId,
      base_amount: data.baseAmount,
    });
    const empUrl = data.regionId
      ? `/api/employee-company-data-by-region?${new URLSearchParams({ source_region_id: data.regionId })}`
      : `/api/employee-company-data?${new URLSearchParams({
          job_title_id: data.jobTitleId,
          territory_id: data.territoryId,
          time_period_id: data.timePeriodId,
        })}`;
    const historyParams = new URLSearchParams({
      job_title_id: data.jobTitleId,
      territory_id: data.territoryId,
      time_period_id: data.timePeriodId,
    });

    Promise.all([
      fetch(`/api/calculation-breakdown?${breakdownParams}`).then((r) => r.json()),
      fetch(empUrl).then((r) => r.json()),
      fetch(`/api/calculation-history?${historyParams}`).then((r) => r.json()),
    ])
      .then(([breakdown, employee, history]) => {
        setFields(breakdown || []);
        setEmployeeData(employee);
        setHistoryRows(history || []);
        setLoadingBreakdown(false);
      })
      .catch(() => setLoadingBreakdown(false));
  }, [data]);

  const toggleCalculation = () => {
    const next = !showCalculation;
    setShowCalculation(next);
    if (next && !detail) {
      setLoadingDetail(true);
      const params = new URLSearchParams({
        territory_id: data.territoryId,
        time_period_id: data.timePeriodId,
        base_amount: data.baseAmount,
      });
      fetch(`/api/calculation-detail?${params}`)
        .then((r) => r.json())
        .then((d) => {
          setDetail(d);
          setLoadingDetail(false);
        })
        .catch(() => setLoadingDetail(false));
    }
  };

  if (!data) return null;

  return (
    <div className="calcpop-backdrop" onClick={onClose}>
      <div className="calcpop-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Fully Inclusive Cost">
        <button type="button" className="calcpop-close" onClick={onClose} aria-label="Close">×</button>
        <h4 className="calcpop-title">Fully Inclusive Cost</h4>
        {data.label && <p className="calcpop-subtitle">{data.label}</p>}

        {loadingBreakdown && <div className="calcpop-status">Loading…</div>}

        {!loadingBreakdown && fields.length === 0 && (
          <div className="calcpop-status">No breakdown returned for this territory/period.</div>
        )}

        {!loadingBreakdown && fields.length > 0 && (
          <table className="calcpop-table">
            <thead>
              <tr><th>Cost Element</th><th>Calculation</th><th className="calcpop-amount-col">Amount</th></tr>
            </thead>
            <tbody>
              {fields.filter((f) => f.value != null && (data.territoryId === 6 || f.value >= 1)).map((f, i) => (
                <tr key={i} className={f.bold ? "calcpop-bold-row" : ""}>
                  <td>{f.label}</td>
                  <td className="calcpop-calc-col">{f.calculation || ""}</td>
                  <td className="calcpop-amount-col">{fmtAmount(f.currency, f.value, data.territoryId)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {employeeData && (
          <table className="calcpop-emp-table">
            <tbody>
              <tr><td>Employee Count</td><td className="calcpop-amount-col">{employeeData.employee_count || 0}</td></tr>
              <tr><td>Company Count</td><td className="calcpop-amount-col">{employeeData.company_count || 0}</td></tr>
            </tbody>
          </table>
        )}

        <div className="calcpop-btn-group">
          <button type="button" className="calcpop-btn" onClick={toggleCalculation}>
            {showCalculation ? "Hide Calculation" : "View Calculation"}
          </button>
          {/* Only rendered when history actually has rows - matches the real
              @if(calculationHistory && calculationHistory.length > 0) gate.
              Will often not appear at all; that's expected (see routes.py). */}
          {historyRows.length > 0 && (
            <button type="button" className="calcpop-btn" onClick={() => setShowHistory((s) => !s)}>
              {showHistory ? "Hide History" : `View History (${historyRows.length})`}
            </button>
          )}
        </div>

        {showCalculation && (
          <div className="calcpop-detail">
            {loadingDetail && <div className="calcpop-status">Loading…</div>}
            {!loadingDetail && !detail && (
              <div className="calcpop-status">No formula breakdown available for this territory.</div>
            )}
            {!loadingDetail && detail && <CalculationDetailSections detail={detail} territoryId={data.territoryId} />}
          </div>
        )}

        {showHistory && (
          <div className="calcpop-history">
            <table className="calcpop-table">
              <thead><tr><th>Date</th><th>L1</th><th>L2</th><th>L3</th></tr></thead>
              <tbody>
                {historyRows.map((h, i) => (
                  <tr key={i}>
                    <td>{h.create_date ? new Date(h.create_date).toLocaleDateString("en-GB") : "-"}</td>
                    <td className="calcpop-amount-col">{h.l1_base_amount}</td>
                    <td className="calcpop-amount-col">{h.l2_base_amount}</td>
                    <td className="calcpop-amount-col">{h.l3_base_amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
